import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const formatDuration = (totalSeconds) => {
  if (totalSeconds == null) return '--:--';
  const seconds = Math.max(0, totalSeconds);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }

  return `${minutes}:${String(secs).padStart(2, '0')}`;
};

const formatTime = (timestamp) => {
  if (!timestamp) return 'TBD';
  return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatChatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatPresenceLabel = (timestamp) => {
  if (!timestamp) return '';
  const diffSeconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
  if (diffSeconds <= 5) return 'Active now';
  if (diffSeconds < 60) return `Active ${diffSeconds}s ago`;
  const minutes = Math.floor(diffSeconds / 60);
  return `Active ${minutes}m ago`;
};

const BroadcasterDashboard = () => {
  const { user, logout } = useAuth();
  const canBroadcast = user?.roles?.some((role) => ['broadcaster', 'super-admin', 'admin'].includes(role.slug));
  const canForceEnd = user?.roles?.some((role) => ['super-admin', 'admin'].includes(role.slug));

  const [profile, setProfile] = useState(null);
  const [profileError, setProfileError] = useState('');
  const [sessionState, setSessionState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [banner, setBanner] = useState(null);
  const [tick, setTick] = useState(0);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(true);
  const [chatError, setChatError] = useState('');
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const chatLatestIdRef = useRef(null);
  const chatScrollRef = useRef(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [presenceError, setPresenceError] = useState('');
  const presenceWindowRef = useRef(120);
  const [monitoring, setMonitoring] = useState(null);
  const [monitorLoading, setMonitorLoading] = useState(true);
  const [monitorError, setMonitorError] = useState('');

  const fetchProfile = useCallback(async () => {
    if (!canBroadcast) return;

    try {
      const { data } = await axios.get('/broadcast/profile');
      setProfile(data.profile);
      setProfileError('');
    } catch (error) {
      if (error.response?.status === 404) {
        setProfile(null);
        setProfileError('No ingest profile yet. Ask an admin to provision your RTMP credentials.');
      } else {
        setProfileError('Unable to load broadcast profile. Try refreshing.');
      }
    }
  }, [canBroadcast]);

  const fetchSession = useCallback(async () => {
    if (!canBroadcast) return;

    try {
      const { data } = await axios.get('/broadcast/session');
      setSessionState(data);
    } catch (error) {
      setBanner({
        tone: 'danger',
        message: 'Unable to sync session state. Please refresh.',
      });
    }
  }, [canBroadcast]);

  const hydrate = useCallback(async () => {
    if (!canBroadcast) {
      setLoading(false);
      return;
    }

    setLoading(true);
    await Promise.all([fetchProfile(), fetchSession()]);
    setLoading(false);
  }, [canBroadcast, fetchProfile, fetchSession]);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  useEffect(() => {
    const timer = setInterval(() => setTick((value) => value + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!canBroadcast) return undefined;

    const poller = setInterval(() => {
      fetchSession();
    }, 15000);

    return () => clearInterval(poller);
  }, [canBroadcast, fetchSession]);

  useEffect(() => {
    if (!banner) return undefined;

    const timeout = setTimeout(() => setBanner(null), 5000);
    return () => clearTimeout(timeout);
  }, [banner]);

  const fetchMonitor = useCallback(async () => {
    if (!canBroadcast) return;
    try {
      const { data } = await axios.get('/broadcast/monitor');
      setMonitoring(data);
      setMonitorError('');
    } catch (error) {
      setMonitorError('Unable to load monitoring telemetry.');
    } finally {
      setMonitorLoading(false);
    }
  }, [canBroadcast]);

  useEffect(() => {
    if (!canBroadcast) {
      setMonitoring(null);
      setMonitorLoading(false);
      return undefined;
    }

    fetchMonitor();
    const poller = setInterval(() => {
      fetchMonitor();
    }, 10000);

    return () => clearInterval(poller);
  }, [canBroadcast, fetchMonitor]);

  const sendPresenceHeartbeat = useCallback(async () => {
    if (!canBroadcast) return;

    try {
      await axios.post('/broadcast/presence');
      setPresenceError('');
    } catch (error) {
      setPresenceError('Presence sync interrupted. Reconnecting...');
    }
  }, [canBroadcast]);

  const fetchPresence = useCallback(async () => {
    if (!canBroadcast) return;

    try {
      const { data } = await axios.get('/broadcast/presence');
      setOnlineUsers(data.users || []);
      if (data.window_seconds) {
        presenceWindowRef.current = data.window_seconds;
      }
      setPresenceError('');
    } catch (error) {
      setOnlineUsers((prev) => prev);
      setPresenceError('Unable to load online broadcasters.');
    }
  }, [canBroadcast]);

  useEffect(() => {
    if (!canBroadcast) {
      setOnlineUsers([]);
      setPresenceError('');
      return undefined;
    }

    sendPresenceHeartbeat();
    const heartbeat = setInterval(() => {
      sendPresenceHeartbeat();
    }, 30000);

    return () => clearInterval(heartbeat);
  }, [canBroadcast, sendPresenceHeartbeat]);

  useEffect(() => {
    if (!canBroadcast) {
      setOnlineUsers([]);
      return undefined;
    }

    fetchPresence();
    const poller = setInterval(() => {
      fetchPresence();
    }, 10000);

    return () => clearInterval(poller);
  }, [canBroadcast, fetchPresence]);

  const fetchChat = useCallback(
    async ({ reset = false } = {}) => {
      if (!canBroadcast) return;

      if (reset) {
        chatLatestIdRef.current = null;
        setChatLoading(true);
      }

      const params = {};
      if (!reset && chatLatestIdRef.current) {
        params.after = chatLatestIdRef.current;
      }

      try {
        const config = {};
        if (Object.keys(params).length) {
          config.params = params;
        }
        const { data } = await axios.get('/broadcast/messages', config);
        if (reset) {
          setChatMessages(data.messages || []);
        } else if (data.messages?.length) {
          setChatMessages((prev) => [...prev, ...data.messages]);
        }

        if (data.latest_id) {
          chatLatestIdRef.current = data.latest_id;
        }

        setChatError('');
      } catch (error) {
        if (reset) {
          setChatMessages([]);
        }
        setChatError('Unable to load chat history.');
      } finally {
        if (reset) {
          setChatLoading(false);
        }
      }
    },
    [canBroadcast]
  );

  useEffect(() => {
    if (!canBroadcast) {
      setChatMessages([]);
      setChatLoading(false);
      chatLatestIdRef.current = null;
      return undefined;
    }

    fetchChat({ reset: true });
    const chatPoller = setInterval(() => {
      fetchChat();
    }, 10000);

    return () => clearInterval(chatPoller);
  }, [canBroadcast, fetchChat]);

  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [chatMessages]);

  const handleSendChatMessage = useCallback(async () => {
    const trimmed = chatInput.trim();
    if (!trimmed) {
      return;
    }

    try {
      setChatSending(true);
      const { data } = await axios.post('/broadcast/messages', { body: trimmed });
      setChatInput('');
      setChatMessages((prev) => [...prev, data.data]);
      chatLatestIdRef.current = data.data.id;
      setChatError('');
    } catch (error) {
      setChatError(error.response?.data?.message || 'Unable to send message right now.');
    } finally {
      setChatSending(false);
    }
  }, [chatInput]);

  const handleChatSubmit = useCallback(
    (event) => {
      event.preventDefault();
      handleSendChatMessage();
    },
    [handleSendChatMessage]
  );

  const handleChatKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendChatMessage();
    }
  };

  const onlineUserIds = useMemo(() => new Set(onlineUsers.map((entry) => entry.user?.id).filter(Boolean)), [onlineUsers]);
  const presenceWindowMinutes = Math.max(1, Math.round((presenceWindowRef.current || 60) / 60));

  const computeAudioLevel = (peak) => {
    if (peak == null) return null;
    const clamped = Math.max(-60, Math.min(0, peak));
    return Math.round(((clamped + 60) / 60) * 100);
  };

  const activeSession = useMemo(() => {
    if (!sessionState || !user?.id) return null;

    const mine = sessionState.your_session;
    const isActive = (session) => session && ['prepping', 'live'].includes(session.status);

    if (isActive(mine)) {
      return mine;
    }

    if (sessionState.active_session?.user?.id === user.id && isActive(sessionState.active_session)) {
      return sessionState.active_session;
    }

    return null;
  }, [sessionState, user]);

  const prepCountdown = useMemo(() => {
    if (!activeSession || activeSession.status !== 'prepping' || !activeSession.prep_expires_at) {
      return null;
    }

    const diff = Math.floor((new Date(activeSession.prep_expires_at).getTime() - Date.now()) / 1000);
    return diff > 0 ? diff : 0;
  }, [activeSession, tick]);

  const liveDuration = useMemo(() => {
    if (!activeSession || activeSession.status !== 'live' || !activeSession.live_started_at) {
      return null;
    }

    const diff = Math.floor((Date.now() - new Date(activeSession.live_started_at).getTime()) / 1000);
    return diff > 0 ? diff : 0;
  }, [activeSession, tick]);

  const occupant = sessionState?.active_session;
  const inUseByOther = occupant && occupant.user?.id !== user?.id && ['prepping', 'live'].includes(occupant.status);

  const handleAction = useCallback(
    async (endpoint) => {
      try {
        setActionLoading(true);
        const { data } = await axios.post(endpoint);
        setBanner({
          tone: 'success',
          message: data.message || 'Action completed.',
        });
        await fetchSession();
      } catch (error) {
        const message =
          error.response?.data?.message ||
          'Unable to update broadcast session right now. Please retry.';
        setBanner({
          tone: 'danger',
          message,
        });

        await fetchSession();
      } finally {
        setActionLoading(false);
      }
    },
    [fetchSession]
  );

  const handleCopy = async (value, label) => {
    if (!value) return;
    if (!navigator?.clipboard?.writeText) {
      setBanner({
        tone: 'danger',
        message: 'Clipboard access is blocked by this browser.',
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      setBanner({
        tone: 'success',
        message: `${label} copied to clipboard.`,
      });
    } catch {
      setBanner({
        tone: 'danger',
        message: 'Clipboard access is blocked by this browser.',
      });
    }
  };

  if (!canBroadcast) {
    return (
      <section style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        <div className="card">
          <h2 style={{ fontSize: '1.75rem', marginBottom: '0.35rem' }}>Broadcast console</h2>
          <p className="text-muted">
            Only accounts with the broadcaster role can control live sessions. Ask an admin to invite you as an
            on-air talent.
          </p>
        </div>

        <div className="card">
          <h3>Access restricted</h3>
          <p className="text-muted" style={{ marginTop: '0.5rem' }}>
            You are currently assigned the roles:{' '}
            {user?.roles?.map((role) => role.slug).join(', ') || 'none'}.
          </p>
        </div>
      </section>
    );
  }

  if (loading) {
    return (
      <section>
        <div className="card" style={{ textAlign: 'center' }}>
          Syncing your broadcast console...
        </div>
      </section>
    );
  }

  const status = activeSession?.status ?? 'idle';
  const canClaim = !!sessionState?.can_claim;
  const showGoLive = status === 'prepping';
  const showEnd = status === 'prepping' || status === 'live';
  const showForceEnd = canForceEnd && !!sessionState?.active_session;
  const prepWindowMinutes = sessionState?.prep_window_seconds
    ? Math.round(sessionState.prep_window_seconds / 60)
    : 5;

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1.5rem' }}>
          <div>
            <h2 style={{ fontSize: '1.8rem', marginBottom: '0.35rem' }}>Broadcast console</h2>
            <p className="text-muted">
              Reserve {prepWindowMinutes}-minute prep windows, start/stop the live feed, and keep the runway clear for the next host.
            </p>
          </div>
          <button type="button" className="btn btn-muted" onClick={logout}>
            Log out
          </button>
        </div>
      </div>

      {banner && <div className={`notice notice-${banner.tone}`}>{banner.message}</div>}

      {inUseByOther && (
        <div className="notice notice-info">
          <strong>{occupant.user?.name}</strong> is currently {occupant.status === 'live' ? 'on air' : 'prepping'}.
          Next slot opens around {formatTime(sessionState?.release_eta)}.
        </div>
      )}

      {profileError && <div className="notice notice-danger">{profileError}</div>}
      <div className="card monitor-card">
        <div className="monitor-header">
          <div>
            <h3>Channel monitoring</h3>
            <p className="text-muted">
              Keep an eye on live telemetry, audio levels, and the scheduled rundown—even if automation is airing a tape.
            </p>
          </div>
          <div className={`status-pill ${monitoring?.monitor?.status === 'live' ? 'live' : 'idle'}`}>
            {monitoring?.monitor?.status === 'live' ? 'Live' : 'Offline'}
          </div>
        </div>

        {monitorError && <p className="chat-error">{monitorError}</p>}

        {monitorLoading ? (
          <div className="monitor-placeholder">Syncing monitoring feed...</div>
        ) : (
          <div className="monitor-grid">
            <div className="monitor-preview">
              {monitoring?.monitor?.monitor_url ? (
                <video
                  src={monitoring.monitor.monitor_url}
                  className="monitor-video"
                  controls
                  muted
                  playsInline
                  autoPlay
                  poster={monitoring.monitor.preview_image_url}
                />
              ) : monitoring?.monitor?.preview_image_url ? (
                <img
                  src={monitoring.monitor.preview_image_url}
                  alt="Current preview"
                  className="monitor-video"
                  style={{ objectFit: 'cover' }}
                />
              ) : (
                <div className="monitor-placeholder">No monitor feed configured.</div>
              )}
            </div>

            <div className="monitor-metrics">
              <div className="metric-block">
                <h4>Program feed</h4>
                <p className="text-muted">
                  {monitoring?.playback?.current?.title || 'Unknown program'} ·{' '}
                  {monitoring?.playback?.current?.type ?? 'scheduled'}
                </p>
                {monitoring?.session?.status ? (
                  <p className="metric-highlight">
                    {monitoring.session.status === 'live'
                      ? `Live host: ${monitoring.session.user?.name ?? '—'}`
                      : `Session: ${monitoring.session.status}`}
                  </p>
                ) : (
                  <p className="metric-highlight">Automation is running the scheduled slate.</p>
                )}
              </div>

              <div className="metric-block">
                <h4>Video</h4>
                <ul className="metric-list">
                  <li>
                    <span>Resolution</span>
                    <strong>{monitoring?.monitor?.video_resolution ?? '—'}</strong>
                  </li>
                  <li>
                    <span>Frame rate</span>
                    <strong>
                      {monitoring?.monitor?.frame_rate ? `${monitoring.monitor.frame_rate} fps` : '—'}
                    </strong>
                  </li>
                  <li>
                    <span>Bitrate</span>
                    <strong>
                      {monitoring?.monitor?.video_bitrate_kbps
                        ? `${monitoring.monitor.video_bitrate_kbps} kbps`
                        : '—'}
                    </strong>
                  </li>
                  <li>
                    <span>Dropped frames</span>
                    <strong>{monitoring?.monitor?.dropped_frames ?? '—'}</strong>
                  </li>
                </ul>
              </div>

              <div className="metric-block">
                <h4>Audio peaks</h4>
                <div className="audio-meter">
                  <div className="audio-meter-row">
                    <span>L</span>
                    <div className="audio-bar">
                      <div
                        className="audio-bar-fill"
                        style={{ width: `${computeAudioLevel(monitoring?.monitor?.audio_peak_left) ?? 0}%` }}
                      />
                    </div>
                    <span className="audio-value">
                      {monitoring?.monitor?.audio_peak_left != null
                        ? `${monitoring.monitor.audio_peak_left.toFixed(1)} dB`
                        : '—'}
                    </span>
                  </div>
                  <div className="audio-meter-row">
                    <span>R</span>
                    <div className="audio-bar">
                      <div
                        className="audio-bar-fill"
                        style={{ width: `${computeAudioLevel(monitoring?.monitor?.audio_peak_right) ?? 0}%` }}
                      />
                    </div>
                    <span className="audio-value">
                      {monitoring?.monitor?.audio_peak_right != null
                        ? `${monitoring.monitor.audio_peak_right.toFixed(1)} dB`
                        : '—'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="broadcast-grid">
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <header>
            <div
              className={`status-pill ${
                status === 'live' ? 'live' : status === 'prepping' ? 'prepping' : 'idle'
              }`}
            >
              {status === 'live' && 'Live'}
              {status === 'prepping' && 'Prepping'}
              {status === 'idle' && 'Idle'}
            </div>
            <h3 style={{ marginTop: '0.5rem' }}>On-air status</h3>
            <p className="text-muted">
              {status === 'live' && 'You are streaming right now.'}
              {status === 'prepping' && 'You have the floor. Configure OBS before the timer expires.'}
              {status === 'idle' && 'Claim a prep slot to avoid overlapping with the next broadcaster.'}
            </p>
          </header>

          <div className="session-meta">
            <div>
              <p className="text-muted">Prep window</p>
              <h4>{prepCountdown != null ? formatDuration(prepCountdown) : '--:--'}</h4>
            </div>
            <div>
              <p className="text-muted">Live duration</p>
              <h4>{liveDuration != null ? formatDuration(liveDuration) : '--:--'}</h4>
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => handleAction('/broadcast/session/claim')}
              disabled={actionLoading || !canClaim}
            >
              {status === 'prepping' ? 'Extend prep window' : `Lock ${prepWindowMinutes}-min prep`}
            </button>

            {showGoLive && (
              <button
                type="button"
                className="btn btn-muted"
                onClick={() => handleAction('/broadcast/session/start')}
                disabled={actionLoading || !profile || !!profileError}
              >
                Go live
              </button>
            )}

            {showEnd && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => handleAction('/broadcast/session/end')}
                disabled={actionLoading}
              >
                End session
              </button>
            )}

            {showForceEnd && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => {
                  if (window.confirm('Force stop the current live session for everyone?')) {
                    handleAction('/broadcast/session/force-end');
                  }
                }}
                disabled={actionLoading}
              >
                Force stop live
              </button>
            )}
          </div>
        </div>

        <div className="card">
          <h3>Ingest profile</h3>
          {profile ? (
            <>
              <div className="profile-grid">
                <div>
                  <p className="text-muted">RTMP server</p>
                  <p style={{ fontWeight: 600 }}>{profile.ingest_server}</p>
                  <button
                    type="button"
                    className="copy-button"
                    onClick={() => handleCopy(profile.ingest_server, 'Server')}
                  >
                    Copy server
                  </button>
                </div>
                <div>
                  <p className="text-muted">Stream key</p>
                  <p style={{ fontWeight: 600 }}>{profile.stream_key}</p>
                  <button
                    type="button"
                    className="copy-button"
                    onClick={() => handleCopy(profile.stream_key, 'Stream key')}
                  >
                    Copy key
                  </button>
                </div>
                <div>
                  <p className="text-muted">Scene</p>
                  <p style={{ fontWeight: 600 }}>{profile.obs_scene || 'Default'}</p>
                </div>
                <div>
                  <p className="text-muted">Output profile</p>
                  <p style={{ fontWeight: 600 }}>{profile.obs_profile || 'Default'}</p>
                </div>
              </div>
              <div style={{ marginTop: '1rem' }}>
                <p className="text-muted">Full ingest URL</p>
                <p style={{ fontWeight: 600, wordBreak: 'break-all' }}>{profile.ingest_url}</p>
                <button
                  type="button"
                  className="copy-button"
                  onClick={() => handleCopy(profile.ingest_url, 'Ingest URL')}
                >
                  Copy URL
                </button>
              </div>
              <p className="text-muted" style={{ marginTop: '0.75rem' }}>
                Last rotated: {profile.last_rotated_at ? new Date(profile.last_rotated_at).toLocaleString() : 'N/A'}
              </p>
            </>
          ) : (
            <p className="text-muted">
              Waiting for ingest credentials. The admin console can create a broadcast profile for you.
            </p>
          )}
        </div>

        <div className="card">
          <h3>Runway protection</h3>
          <p className="text-muted">
            Only one broadcaster can prep or go live at a time. Release the slot as soon as you wrap so the next host can
            set up without disruption.
          </p>

          <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
            <div className="queue-chip">
              <span role="img" aria-hidden="true">
                🎤
              </span>
              {occupant
                ? `${occupant.user?.name} · ${occupant.status}`
                : 'No one is holding the queue'}
            </div>
            <div className="queue-chip">
              <span role="img" aria-hidden="true">
                ⏱️
              </span>
              Next release:{' '}
              {sessionState?.release_eta ? formatTime(sessionState.release_eta) : 'Available now'}
            </div>
          </div>
        </div>

        <div className="card presence-card">
          <h3>Who’s online</h3>
          <p className="text-muted">
            Anyone active in the last {presenceWindowMinutes}-minute window appears here. Keep the runway clear and let
            each other know when you wrap.
          </p>
          {presenceError && <p className="chat-error">{presenceError}</p>}
          {onlineUsers.length ? (
            <ul className="presence-list">
              {onlineUsers.map((entry) => (
                <li key={entry.user?.id || entry.last_seen_at} className="presence-list-item">
                  <div className="presence-user">
                    <span className="presence-dot online" />
                    <div>
                      <strong>{entry.user?.name || 'Unknown user'}</strong>
                      <p className="text-muted">{formatPresenceLabel(entry.last_seen_at)}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted">No broadcasters are online in the current window.</p>
          )}
        </div>

        <div className="card chat-panel">
          <h3>Backstage chat</h3>
          <p className="text-muted">
            Share quick handoffs or heads-up messages so the next host knows when the runway is clear.
          </p>

          <div className="chat-messages" ref={chatScrollRef}>
            {chatLoading ? (
              <p className="text-muted">Loading chat history...</p>
            ) : chatMessages.length ? (
              chatMessages.map((message) => (
                <div key={message.id} className="chat-message">
                  <div className="chat-message-header">
                    <div className="chat-message-author">
                      <strong>{message.user?.name || 'Unknown user'}</strong>
                      <span
                        className={`presence-pill ${
                          onlineUserIds.has(message.user?.id) ? 'presence-online' : 'presence-offline'
                        }`}
                      >
                        {onlineUserIds.has(message.user?.id) ? 'Online' : 'Offline'}
                      </span>
                    </div>
                    <span className="text-muted">{formatChatTimestamp(message.created_at)}</span>
                  </div>
                  <p>{message.body}</p>
                </div>
              ))
            ) : (
              <p className="text-muted">No messages yet. Let everyone know when you wrap or go live.</p>
            )}
          </div>

          {chatError && <p className="chat-error">{chatError}</p>}

          <form className="chat-form" onSubmit={handleChatSubmit}>
            <textarea
              className="chat-input"
              placeholder="Type a quick update and hit Enter to send"
              value={chatInput}
              onChange={(event) => setChatInput(event.target.value)}
              onKeyDown={handleChatKeyDown}
              rows={2}
              maxLength={2000}
              disabled={chatSending}
            />
            <button type="submit" className="btn btn-primary" disabled={chatSending || !chatInput.trim()}>
              {chatSending ? 'Sending...' : 'Send'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default BroadcasterDashboard;
